import { publishEvent } from './sync';
import { getCity } from './location';
import type { PostPayload } from './events';
import { getAlgorithmConfig, applyAuthorCap } from './signals';
import { getLikeCount } from './likes';

export type { PostPayload };

const POSTS_KEY = 'authenticulture_posts';
const MAX_POSTS = 500;

export interface Post {
  id: string;
  pubkey: string;
  sig: string;
  timestamp: number;
  content: string;
  city?: string;
  magnetURI?: string;
  mediaType?: 'image' | 'audio' | 'video';
  replyTo?: { pubkey: string; postId: string };
}

export type PostFeedMode = 'foryou' | 'trending' | 'following' | 'local' | 'fresh';

function loadPosts(): Post[] {
  try {
    return JSON.parse(localStorage.getItem(POSTS_KEY) ?? '[]');
  } catch {
    return [];
  }
}

export function savePost(event: {
  id: string;
  pubkey: string;
  sig: string;
  timestamp: number;
  payload: PostPayload;
}): void {
  const p = event.payload;
  const post: Post = {
    id: event.id,
    pubkey: event.pubkey,
    sig: event.sig,
    timestamp: event.timestamp,
    content: p.content,
    city: p.city,
    magnetURI: p.magnetURI,
    mediaType: p.mediaType,
    replyTo: p.replyTo,
  };
  const posts = loadPosts();
  if (posts.some(q => q.id === post.id)) return;
  posts.unshift(post);
  if (posts.length > MAX_POSTS) posts.splice(MAX_POSTS);
  localStorage.setItem(POSTS_KEY, JSON.stringify(posts));
}

export function getPost(id: string): Post | null {
  return loadPosts().find(p => p.id === id) ?? null;
}

export function getPostsByCity(city: string, limit = 50): Post[] {
  return loadPosts()
    .filter(p => p.city === city)
    .slice(0, limit);
}

export function getThread(rootPostId: string): Post[] {
  const posts = loadPosts();
  const root = posts.find(p => p.id === rootPostId);
  if (!root) return [];
  const result: Post[] = [root];
  const collect = (parentId: string) => {
    const replies = posts
      .filter(p => p.replyTo?.postId === parentId)
      .sort((a, b) => a.timestamp - b.timestamp);
    for (const r of replies) {
      result.push(r);
      collect(r.id);
    }
  };
  collect(rootPostId);
  return result;
}

function getFollowedPubkeys(userId: string): Set<string> {
  try {
    const follows = JSON.parse(localStorage.getItem('authenticulture_follows') ?? '[]') as Array<{
      followerUserId: string;
      followeeUserId: string;
    }>;
    return new Set(follows.filter(f => f.followerUserId === userId).map(f => f.followeeUserId));
  } catch {
    return new Set();
  }
}

export function getFeedPosts(mode: PostFeedMode, userId: string, limit = 50): Post[] {
  const posts = loadPosts().filter(p => !p.replyTo);

  switch (mode) {
    case 'fresh':
      return posts.slice(0, limit);

    case 'local': {
      const city = getCity()?.city;
      if (!city) return posts.slice(0, limit);
      return posts.filter(p => p.city === city).slice(0, limit);
    }

    case 'following': {
      const followed = getFollowedPubkeys(userId);
      return posts.filter(p => followed.has(p.pubkey)).slice(0, limit);
    }

    case 'trending': {
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      const recentPosts = posts.filter(p => p.timestamp >= cutoff);
      const signalCounts = new Map<string, number>();
      try {
        const signals = JSON.parse(localStorage.getItem('authenticulture_signals') ?? '[]') as Array<{
          hash: string;
          ts: number;
        }>;
        for (const s of signals) {
          if (s.ts >= cutoff) signalCounts.set(s.hash, (signalCounts.get(s.hash) ?? 0) + 1);
        }
      } catch { }
      return recentPosts
        .sort((a, b) => (signalCounts.get(b.id) ?? 0) - (signalCounts.get(a.id) ?? 0))
        .slice(0, limit);
    }

    case 'foryou': {
      const config = getAlgorithmConfig();
      let creatorAffinity: Record<string, number> = {};
      try {
        const profile = JSON.parse(localStorage.getItem('authenticulture_interest_profile') ?? '{}');
        creatorAffinity = profile.creators ?? {};
      } catch { }
      const city = getCity()?.city;
      const scored = posts.map(p => {
        const ageHours = (Date.now() - p.timestamp) / 3_600_000;
        const decay = 1 / Math.pow(ageHours + 2, config.gravity);
        const affinity = (creatorAffinity[p.pubkey] ?? 0) * 0.4;
        const likes = getLikeCount(p.id) * config.likeWeight * 0.4;
        const local = (p.city && p.city === city ? 1 : 0) * 0.2;
        return { post: p, score: (affinity + likes + local) * decay };
      }).sort((a, b) => b.score - a.score).map(s => s.post);
      return applyAuthorCap(scored, p => p.pubkey, config.maxPerAuthor).slice(0, limit);
    }

    default:
      return posts.slice(0, limit);
  }
}

export function deletePost(postId: string, pubkey: string): boolean {
  const posts = loadPosts();
  const index = posts.findIndex(p => p.id === postId && p.pubkey === pubkey);
  if (index === -1) return false;
  posts.splice(index, 1);
  localStorage.setItem(POSTS_KEY, JSON.stringify(posts));
  return true;
}

export function forceDeletePost(postId: string): boolean {
  const posts = loadPosts();
  const index = posts.findIndex(p => p.id === postId);
  if (index === -1) return false;
  posts.splice(index, 1);
  localStorage.setItem(POSTS_KEY, JSON.stringify(posts));
  return true;
}

const PINNED_POSTS_KEY = 'authenticulture_pinned_posts';

export function getPinnedPostIds(): string[] {
  try {
    return JSON.parse(localStorage.getItem(PINNED_POSTS_KEY) ?? '[]');
  } catch {
    return [];
  }
}

export function pinPost(postId: string): void {
  const ids = getPinnedPostIds();
  if (!ids.includes(postId)) {
    localStorage.setItem(PINNED_POSTS_KEY, JSON.stringify([...ids, postId]));
  }
}

export function unpinPost(postId: string): void {
  const ids = getPinnedPostIds().filter(id => id !== postId);
  localStorage.setItem(PINNED_POSTS_KEY, JSON.stringify(ids));
}

export async function publishPost(
  content: string,
  options: {
    magnetURI?: string;
    mediaType?: 'image' | 'audio' | 'video';
    replyTo?: { pubkey: string; postId: string };
  } = {}
): Promise<void> {
  const city = getCity()?.city;
  const payload: PostPayload = {
    content,
    ...(city ? { city } : {}),
    ...options,
  };
  await publishEvent('post', payload as any);
}
